<template>
    <div class="w-full space-y-3">
        <Head title="Reports"/>
        <div class="flex justify-between">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Client Quote Report</h1>
            <a :href="route('frontend.reports.operational_client_quote_report_export', {
                _query: {
                    starts_at: form.starts_at,
                    ends_at: form.ends_at,
                    client: form.client,
                    manager: form.manager,
                    quote_status: form.quote_status,
                    trading_entity: form.trading_entity
                }})" class="py-3 px-3 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral w-40 text-center">Download</a>
        </div>
    </div>
    <br />
    <div class="grid grid-cols-3 gap-2">
        <div class="relative" v-if="trading_details.length > 1">
            <label for="trading-entity" class="block text-sm font-medium text-gray-700">Trading Entity</label>
            <select id="trading-entity" v-model="form.trading_entity"
                class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                <option value="" v-html="'All'"></option>
                <option v-for="(trading_detail, index) in trading_details" :key="index" :value="trading_detail.id" :selected="form.trading_entity === trading_detail.id" v-html="trading_detail.trading_name"></option>
            </select>
        </div>
        <div class="relative">
            <label  for="start-date" class="block text-sm font-medium text-gray-700">Start Date <span v-if="formattedStartDate">({{ formattedStartDate }})</span></label>
            <input v-model="form.starts_at" name="start-date" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select start date">
        </div>
        <div class="relative">
            <label for="end-date" class="block text-sm font-medium text-gray-700">End Date <span v-if="formattedEndDate">({{ formattedEndDate }})</span></label>
            <input v-model="form.ends_at" name="end-date" type="date" class="mt-2 bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 datepicker-input" placeholder="Select end date">
        </div>
    </div>
    <div class="flex gap-2 w-full items-center mt-2">
        <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="clients" label="name" :reduce="clients => clients.id" v-model="form.client" placeholder="Clients" />
        <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="managers" label="name" :reduce="managers => managers.id" v-model="form.manager" placeholder="Manager" />
        <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" :options="quoteStatuses" label="label" :reduce="statuses => statuses.value" v-model="form.quote_status" placeholder="Status" />
    </div>
    <br />
    <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6 mt-5">
        <div class="flex flex-col space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Quote Name
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Fixture Date
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Team Manager
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Quote Total
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Quote Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="Object.values(quotes).length > 0" v-for="(quote, index) in quotes.data" :key="index"
                                    :class="{'bg-gray-100': index % 2}" class="hover:bg-gray-50 cursor-pointer">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <a :href="route('frontend.fixture.quote.show' , [quote.fixture_id, quote.id])">
                                            {{ quote.name ?? 'N/A' }}
                                        </a>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="quote.fixture_title">{{ quote.fixture_title }}</span>
                                        <span v-else>N/A</span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="quote.fixture">{{ moment(quote.fixture.starts_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}</span>
                                        <span v-else>N/A</span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ quote.fixture?.travel_manager?.name ?? 'N/A' }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ formatCurrency(quote.subtotals.gross_sell_total, quote.sell_currency.code, true) }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ quote.status ?? 'N/A' }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="quotes.links"/>
        </div>
    </div>
</template>

<script>
    import Authenticated from "@/Layouts/Authenticated";
    import {Link, Head, useForm} from "@inertiajs/vue3";
    import Pagination from "@/Components/Pagination";
    import mapValues from "lodash/mapValues";
    import debounce from "lodash/debounce";
    import pickBy from "lodash/pickBy";
    import { objectToString } from "@vue/shared";
    import moment from "moment/moment";

    export default {
        layout: Authenticated,
        components: {
            Head,
            Link,
            Pagination
        },
        props: ['starts_at', 'ends_at', 'quotes', 'clients', 'managers', 'quote_statuses', 'client_id', 'manager_id', 'quote_status', 'selected_client', 'selected_manager', 'trading_details'],
        data(props) {
            return {
                form: {
                    starts_at: props.starts_at ?? null,
                    ends_at: props.ends_at ?? null,
                    client: props.selected_client ?? null,
                    manager: props.selected_manager ?? null,
                    quote_status: props.quote_status ?? null,
                    trading_entity: null
                },
                quoteStatuses: Object.entries(props.quote_statuses).map(status => {return{label: status[1], value: status[0]}})
            }
        },
        methods: {
            reset() {
                this.form = mapValues(this.form, () => null)
            },
            number_format(nStr) {
                nStr += '';
                let x = nStr.split('.');
                let x1 = x[0];
                let x2 = x.length > 1 ? '.' + x[1] : '';
                let rgx = /(\d+)(\d{3})/;
                while (rgx.test(x1)) {
                    x1 = x1.replace(rgx, '$1' + ',' + '$2');
                }
                return x1 + x2;
            },
        },
        watch: {
            form: {
                deep: true,
                handler: debounce(function () {
                    this.$inertia.get(route('frontend.reports.operational_client_quote_report'), pickBy(this.form), {preserveState: true, preserveScroll: true})
                }, 500),
            },
        },
        created() {
            this.moment = moment;
        },
        computed: {
            formattedStartDate() {
                if (this.form.starts_at) {
                    return moment(this.form.starts_at).format('DD MMM YYYY');
                }
                return '';
            },
            formattedEndDate() {
                if (this.form.ends_at) {
                    return moment(this.form.ends_at).format('DD MMM YYYY');
                }
                return '';
            },
        },
    }
</script>
